import {useState} from "react"
import {AlertTriangle} from "lucide-react"
import {useNavigate} from "react-router-dom"

import {Button} from "@/components/ui/button"
import {Checkbox} from "@/components/ui/checkbox"
import {Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,} from "@/components/ui/dialog"
import {FieldSeparator} from "@/components/ui/field"
import {Info} from "@/components/Info"
import {KepKeyReader} from "@/components/kep/KepKeyReader"
import {ScenarioNotice} from "@/components/ScenarioNotice"
import type {EUSignLibraryAdapter} from "iit/browser"
import {
  adminGrantingPosady,
  connectSubject,
  type KepMatch,
  posadaDisplay,
  type ResolveByKepSubject,
  resolveSubjectByKep,
} from "@/lib/api/subjects"
import {useSession} from "@/store/session"

/**
 * CD1 in login.md: read-a-key step, then one of four confirm outcomes
 * (no-match failure, or one of three responsible-person success outcomes).
 * This is the real counterpart to the wireframe's scenario-driven
 * `ConnectSubjectDialog` — `person`/`subject` come from an actual
 * `resolve-by-kep` call instead of a `personForScenario` lookup.
 */
export function ConnectSubjectDialog({ email }: { email: string }) {
  const navigate = useNavigate()
  const setConnectedSubject = useSession((s) => s.setConnectedSubject)

  const [step, setStep] = useState<"read" | "confirm">("read")
  const [kepAdapter, setKepAdapter] = useState<EUSignLibraryAdapter | null>(
    null
  )
  const [isResolving, setIsResolving] = useState(false)
  const [resolveError, setResolveError] = useState<string | null>(null)
  const [match, setMatch] = useState<KepMatch | null>(null)
  const [subject, setSubject] = useState<ResolveByKepSubject | null>(null)
  const [autoSignConsent, setAutoSignConsent] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const [connectError, setConnectError] = useState<string | null>(null)

  const grantsAdmin = match !== null && adminGrantingPosady.includes(match.posada)

  const resetToReadStep = () => {
    setKepAdapter(null)
    setAutoSignConsent(false)
    setConnectError(null)
    setStep("read")
  }

  // TODO(kep-signature-protocol): login.md's endpoint 4 grounding notes "the
  // exact transport format is outside this project's own documentation and
  // depends on the KEP integration library" — there is no documented
  // nonce/challenge to sign against here yet, so this signs `email` as a
  // stand-in payload. It must be replaced once the backend defines what
  // `kepSignature` actually needs to contain (most likely a server-issued
  // nonce, to prove possession of the key for *this* login attempt).
  const signEmail = (adapter: EUSignLibraryAdapter) =>
    adapter.signWithData(new TextEncoder().encode(email))

  const handleKeyRead = async ({
    adapter,
  }: {
    adapter: EUSignLibraryAdapter
    keyLabel: string
  }) => {
    setKepAdapter(adapter)
    setIsResolving(true)
    setResolveError(null)
    try {
      const kepSignature = await signEmail(adapter)
      const result = await resolveSubjectByKep(kepSignature)
      setMatch(result.match)
      setSubject(result.subject ?? null)
      setStep("confirm")
    } catch {
      setResolveError(
        "Не вдалося перевірити сертифікат за даними ЄДРТЗ. Спробуйте ще раз."
      )
    } finally {
      setIsResolving(false)
    }
  }

  const handleConfirm = async () => {
    if (!kepAdapter) return
    setIsConnecting(true)
    setConnectError(null)
    try {
      const kepSignature = await signEmail(kepAdapter)
      const result = await connectSubject(kepSignature)
      setConnectedSubject(result)
      if (grantsAdmin) {
        navigate("/subject/users")
        return
      }
      navigate("/")
    } catch {
      setConnectError("Не вдалося підключити суб'єкт ОТК. Спробуйте ще раз.")
    } finally {
      setIsConnecting(false)
    }
  }

  return (
    <Dialog
      onOpenChange={(open) => {
        if (!open) resetToReadStep()
      }}
    >
      <DialogTrigger asChild>
        <Button className="w-full">Продовжити з КЕП</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Отримання даних суб'єкта ОТК</DialogTitle>
        </DialogHeader>

        {step === "read" && (
          <>
            {resolveError && (
              <ScenarioNotice tone="danger">
                <AlertTriangle className="mr-2 inline size-4" />
                {resolveError}
              </ScenarioNotice>
            )}
            <KepKeyReader
              actionLabel="Зчитати носій КЕП"
              disabled={isResolving}
              onRead={handleKeyRead}
            />
          </>
        )}

        {step === "confirm" && match === null && (
          <>
            <ScenarioNotice tone="danger">
              <AlertTriangle className="mr-2 inline size-4" />
              Цей сертифікат не знайдено серед відповідальних осіб жодного
              суб'єкта ОТК за даними ЄДРТЗ. Перевірте, чи зчитали ви потрібний
              носій — або попросіть адміністратора вашого суб'єкта надіслати
              запрошення на вашу пошту.
            </ScenarioNotice>
            <DialogFooter>
              <Button onClick={resetToReadStep} variant="outline">
                Спробувати інший ключ
              </Button>
            </DialogFooter>
          </>
        )}

        {step === "confirm" && match !== null && (
          <>
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Info label="Власник ключа" value={match.certOwner} />
                <Info
                  label="Посада за даними ЄДРТЗ"
                  value={posadaDisplay[match.posada]}
                />
                <Info label="Сертифікат" value={match.certSerial} />
              </div>
              <FieldSeparator />
              <div className="pt-2">
                <p className="mb-2 text-sm font-medium">
                  Суб'єкт ОТК за даними ЄДРТЗ
                </p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Info label="Назва" value={subject?.name ?? ""} />
                  <Info label="ЄДРПОУ" value={subject?.edrpou ?? ""} />
                  <Info label="Адреса" value={subject?.address ?? ""} />
                  <Info label="Статус" value={subject?.status ?? ""} />
                </div>
              </div>
              <ScenarioNotice>
                До цього суб'єкта ОТК буде прив'язано вашу пошту {email}.{" "}
                {grantsAdmin
                  ? "Як керівник або заступник керівника ви автоматично отримаєте роль «Адмін» — одразу після підключення ми відкриємо сторінку «Користувачі», щоб ви могли запросити співробітників."
                  : "Ви отримаєте роль «Відповідальна особа суб'єкта ОТК». Доки адміністратор суб'єкта — керівник або заступник керівника — не увійде та не проведе оплату, робота буде в обмеженому режимі."}
              </ScenarioNotice>
              <p className="text-sm text-muted-foreground">
                Для створення Робочого Простору субʼєкта буде надіслано
                декілька додаткових безкоштовних запитів до Державного Реєстру
                по Вашому субʼєкту, які потребують підпису КЕП. Це буде
                зроблено автоматично.
              </p>
              <div className="flex items-start gap-2">
                <Checkbox
                  checked={autoSignConsent}
                  id="connect-subject-auto-sign-consent"
                  onCheckedChange={(checked) =>
                    setAutoSignConsent(checked === true)
                  }
                />
                <label
                  className="text-sm font-medium leading-5 peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  htmlFor="connect-subject-auto-sign-consent"
                >
                  Даю згоду на автоматичний підпис запитів до Реєстру
                </label>
              </div>
              {connectError && (
                <ScenarioNotice tone="danger">
                  <AlertTriangle className="mr-2 inline size-4" />
                  {connectError}
                </ScenarioNotice>
              )}
            </div>
            <DialogFooter>
              <Button onClick={resetToReadStep} variant="outline">
                Спробувати інший ключ
              </Button>
              <Button
                disabled={!autoSignConsent || isConnecting}
                onClick={handleConfirm}
              >
                {isConnecting ? "Підключення…" : "Підтвердити та увійти"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
